import React, { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '../../lib/cn'
import { useToastStore } from '../../stores/toastStore'
import { copyToClipboard } from './copyToClipboard'

export interface CopyButtonProps {
  /** Что копируется по обычному клику. */
  value: string
  /** Название значения: попадает в тост «Скопировано: …» и в aria-label «Скопировать …». */
  label?: string
  /** Ссылка, которая копируется по Shift+клику (например, адрес окна с открытой карточкой). */
  link?: string
  size?: 'xs' | 'sm'
  /** Рисовать рядом с иконкой текст: true — «копировать», строка — свой текст (для плотных блоков). */
  withText?: boolean | string
  className?: string
}

/** Сколько иконка остаётся галочкой после копирования. */
const COPIED_MS = 1500

/**
 * CopyButton — единая кнопка «скопировать» рядом со значением.
 *
 * Одна на весь проект: локальные копии расходились по подписям, тостам и
 * поведению при отказе буфера. Копирование выполняется внутри обработчика
 * клика (требование браузера), при отказе показывается тост об ошибке.
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  label,
  link,
  size = 'sm',
  withText = false,
  className,
}) => {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const name = label ?? value
  const title = link
    ? `Скопировать ${name} (Shift+клик — ссылку)`
    : `Скопировать ${name}`

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Кнопка часто стоит внутри кликабельной строки или ссылки.
    event.stopPropagation()
    event.preventDefault()
    const asLink = event.shiftKey && Boolean(link)
    const text = asLink ? (link as string) : value
    const ok = await copyToClipboard(text)
    const { showToast } = useToastStore.getState()
    if (!ok) {
      showToast('Не удалось скопировать', 'error')
      return
    }
    showToast(`Скопировано: ${asLink ? 'ссылка' : name}`, 'success')
    setCopied(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), COPIED_MS)
  }

  const iconClass = size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5'
  const Icon = copied ? Check : Copy

  return (
    <button
      type="button"
      aria-label={`Скопировать ${name}`}
      title={title}
      onClick={handleClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
        size === 'xs' ? 'p-0.5' : 'p-1',
        copied && 'text-emerald-600 dark:text-emerald-400',
        className,
      )}
    >
      <Icon className={iconClass} />
      {withText && (
        <span className="text-2xs">
          {copied ? 'скопировано' : typeof withText === 'string' ? withText : 'копировать'}
        </span>
      )}
    </button>
  )
}

export default CopyButton
